import { GQL_CLIENT } from 'src/graphqlClient'
import {
  deriveBucket,
  getTaskKindConfig,
} from '@autoriz/shared/assistant'
import { ASSISTANT_TASKS_QUERY } from './assistant.gql'
import { TaskListSchema, type Task, type TaskBucket } from '../schemas/task'

type RawTaskRow = {
  id: string
  project_id: number | null
  kind: string
  source: string
  title: string
  reason: string | null
  meta: string | null
  action_label_override: string | null
  created_at: string
  updated_at: string
  visible_from: string
  target_time: string
  dismissed_at: string | null
  completed_at: string | null
  project: {
    id: number
    project_type: string | null
    primary_signatory_client: {
      first_name: string | null
      last_name: string | null
    } | null
  } | null
}

const toClientName = (
  c: { first_name: string | null; last_name: string | null } | null | undefined
): string | null => {
  if (!c) return null
  const last = (c.last_name ?? '').trim()
  if (last.length > 0) return last.toUpperCase()
  const first = (c.first_name ?? '').trim()
  return first.length > 0 ? first : null
}

const deriveTaskBucket = (row: RawTaskRow, now: Date): TaskBucket => {
  if (row.dismissed_at != null) return 'ignored'
  const config = getTaskKindConfig(row.kind)
  return deriveBucket(new Date(row.target_time), now, config.priorityThresholds)
}

export const fetchAssistantTasks = async (): Promise<Task[]> => {
  const now = new Date()
  const data = (await GQL_CLIENT.request(ASSISTANT_TASKS_QUERY, {
    now: now.toISOString(),
  })) as {
    assistant_tasks: RawTaskRow[]
  }
  const normalized = data.assistant_tasks.map((row: RawTaskRow) => ({
    id: row.id,
    projectId: row.project_id,
    kind: row.kind,
    source: row.source,
    title: row.title,
    reason: row.reason,
    meta: row.meta,
    actionLabelOverride: row.action_label_override,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    visibleFrom: row.visible_from,
    targetTime: row.target_time,
    dismissedAt: row.dismissed_at,
    completedAt: row.completed_at,
    bucket: deriveTaskBucket(row, now),
    client: toClientName(row.project?.primary_signatory_client),
    projectType: row.project?.project_type ?? null,
  }))
  const parsed = TaskListSchema.safeParse(normalized)
  if (!parsed.success) {
    // eslint-disable-next-line no-console
    console.warn('[assistant] Task list failed Zod validation', parsed.error.issues)
    return []
  }
  return parsed.data
}
